import React, { useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatIndiaTime } from '../config/india-timezone';
import { 
  Building2, 
  Plus, 
  RefreshCw, 
  Crown, 
  Shield, 
  User,
  Check,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';

const WorkspaceSelection = () => {
  const { 
    workspaces, 
    currentWorkspace, 
    switchWorkspace, 
    createWorkspace,
    createNowgrayWorkspace,
    refreshWorkspaces,
    loading 
  } = useWorkspace();
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceDescription, setNewWorkspaceDescription] = useState('');
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [creatingNowgray, setCreatingNowgray] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    if (!newWorkspaceName.trim()) return;

    setCreatingWorkspace(true);
    const result = await createWorkspace(newWorkspaceName.trim(), newWorkspaceDescription.trim());
    setCreatingWorkspace(false);
    
    if (result.success) {
      setNewWorkspaceName('');
      setNewWorkspaceDescription('');
      setShowCreateForm(false);
    }
  };

  const handleCreateNowgray = async () => {
    setCreatingNowgray(true);
    const result = await createNowgrayWorkspace();
    setCreatingNowgray(false);
    
    if (result.success) {
      toast.success('Nowgray workspace created successfully!');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    const result = await refreshWorkspaces();
    setRefreshing(false);
    
    if (result.success) {
      toast.success('Workspaces refreshed successfully!');
    } else {
      setError(result.error || 'Failed to refresh workspaces');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'admin':
        return <Shield className="w-4 h-4 text-blue-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'admin':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-600" />
          <p className="text-gray-600">Loading workspaces...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Select Workspace</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Choose a workspace to continue. Each workspace has its own WhatsApp credentials, contacts, and settings.
          </p>
        </div>

        {/* Workspaces Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {workspaces.map((workspace) => (
            <div
              key={workspace.id}
              onClick={() => switchWorkspace(workspace)}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-200 cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getRoleColor(workspace.user_role)}`}>
                  {getRoleIcon(workspace.user_role)}
                  <span className="capitalize">{workspace.user_role}</span>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                {workspace.name}
              </h3>
              
              {workspace.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {workspace.description}
                </p>
              )}
              
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>{workspace.member_count} member{workspace.member_count !== 1 ? 's' : ''}</span>
                <span>Created {formatIndiaTime(workspace.created_at, 'relative')}</span>
              </div>
              
              {currentWorkspace?.id === workspace.id && (
                <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                  <Check className="w-4 h-4 mr-1" />
                  Current Workspace
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading workspaces</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {workspaces.length === 0 && !error && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No workspaces found</h3>
            <p className="text-gray-600 mb-6">
              You don't have access to any workspaces yet. Create a new workspace or ask an admin to invite you.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh Workspaces'}</span>
          </button>

          <button
            onClick={handleCreateNowgray}
            disabled={creatingNowgray}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
          >
            {creatingNowgray ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Building2 className="w-4 h-4" />
            )}
            <span>{creatingNowgray ? 'Creating...' : 'Create Nowgray Workspace'}</span>
          </button>

          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <Plus className="w-4 h-4" />
            <span>Create New Workspace</span>
          </button>
        </div>

        {/* Create Workspace Form */}
        {showCreateForm && (
          <div className="mt-8 max-w-md mx-auto">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Workspace</h3>
              
              <form onSubmit={handleCreateWorkspace} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Workspace Name
                  </label>
                  <input
                    type="text"
                    value={newWorkspaceName}
                    onChange={(e) => setNewWorkspaceName(e.target.value)}
                    placeholder="Enter workspace name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={newWorkspaceDescription}
                    onChange={(e) => setNewWorkspaceDescription(e.target.value)}
                    placeholder="Enter workspace description"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={creatingWorkspace}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    {creatingWorkspace ? 'Creating...' : 'Create Workspace'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewWorkspaceName('');
                      setNewWorkspaceDescription('');
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">
            <strong>Need help?</strong> If you don't see your workspace, ask an admin to invite you. 
            Each workspace has its own WhatsApp credentials and settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSelection;
